import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";

import { Can } from "configs/CASLContext";

import { deleteAppVersion, findAppVersions } from "services/AppVersionService";
import { AppVersionContext } from "components/app-version/AppVersionContext";
import { Confirmation } from "components/Confirmation";
import { AppVersionEditor } from "components/app-version/AppVersionEditor";
import { AppVersion, create, del, edit, Template, X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import { actionUpdate, actionView } from "utils/action";
import { Dropdown } from "primereact/dropdown";
import BlockDetail from "components/block-detail/BlockDetail";

export const AppVersionList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["app-version", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const typeApps = [
        { label: t("version.web"), value: 2 },
        { label: t("version.mobile"), value: 1 },
    ];

    const appVersionContext = useContext(AppVersionContext);
    const { appVersion, resetAppVersion, setAppVersion, appVersions, setAppVersions, submitted, setSubmitted, appVersionDialog, setAppVersionDialog } = appVersionContext;

    const [deleteAppVersionDialog, setDeleteAppVersionDialog] = useState(false);
    const [selectedAppVersion, setSelectedAppVersion] = useState(null);

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(true);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            name: { value: "", matchMode: "ilike" },
            appType: { value: null, matchMode: "equals" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    useEffect(() => {
        if (submitted) {
            loadLazyData();
            setSubmitted(false);
            setSelectedAppVersion(null);
            resetAppVersion();
        }
    }, [submitted]);

    const loadLazyData = () => {
        setLoading(true);
        setAppVersion(null);
        setSelectedAppVersion(null);
        findAppVersions(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                setAppVersions(dataRes);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openNewDialog = () => {
        resetAppVersion();
        setSubmitted(false);
        setAppVersionDialog(true);
    };

    const openEditDialog = () => {
        setAppVersion({ ...selectedAppVersion, action: actionUpdate });
        setSubmitted(false);
        setAppVersionDialog(true);
    };

    const openViewDialog = (appVersion) => {
        setAppVersion({ ...appVersion, action: actionView });
        setSubmitted(false);
        setAppVersionDialog(true);
    };

    const confirmDeleteAppVersion = () => {
        setDeleteAppVersionDialog(true);
    };

    const hideDeleteAppVersionDialog = () => {
        setDeleteAppVersionDialog(false);
    };

    const deleteAppVersionAction = () => {
        deleteAppVersion(headers, selectedAppVersion.id)
            .then(() => {
                setCheckLoad(true);
                setLazyParams({
                    ...lazyParams,
                    page: 0,
                    first: 0,
                });
                setDeleteAppVersionDialog(false);
                setSelectedAppVersion(null);
                resetAppVersion();
                toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.deleted", { ns: "common", object: t("version.titleAppVersionSuccess") }), life: 3000 });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const actionTemplate = () => {
        return (
            <div className="grid">
                <div className="col-12 tex-center mt-2" style={{ display: "flex", justifyContent: "center" }}>
                    {/* tạo mới */}
                    <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                    {/* sửa */}
                    <Button disabled={!selectedAppVersion} label={t("edit", { ns: "common" })} icon="pi pi-pencil" className="p-button mr-2" onClick={openEditDialog} />
                    {/* xóa */}
                    <Button disabled={!selectedAppVersion} label={t("delete", { ns: "common" })} icon="pi pi-trash" className="p-button-danger p-button mr-2" onClick={confirmDeleteAppVersion} />
                </div>
            </div>
        );
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        );
    };

    const typeTemplate = (rowData) => {
        return <div className="flex align-items-center justify-content-center actions">{typeApps.find((e) => e.value === rowData.appType)?.label}</div>;
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("version.list")}</h5>
        </div>
    );

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Can I={edit} a={Template}>
                    {/* view */}
                    <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openViewDialog(rowData)} />
                </Can>
            </div>
        );
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Loại */}
            <div className="col-12 md:col-4">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "66px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="appType"
                    >
                        {t("version.appType")}
                    </label>
                    <Dropdown
                        className="col"
                        value={lazyParams?.filters?.appType?.value}
                        style={{
                            width: "100%",
                            margin: "7px 0px 0px 0px",
                        }}
                        showClear={true}
                        id="appType"
                        name="appType"
                        options={typeApps}
                        onChange={(e) => {
                            setCheckLoad(false);
                            setLazyParams({
                                ...lazyParams,
                                filters: {
                                    ...lazyParams.filters,
                                    appType: {
                                        ...lazyParams.filters.appType,
                                        value: e.value,
                                    },
                                },
                            });
                        }}
                    ></Dropdown>
                </div>
            </div>

            {/* tên */}
            <div className="col-12 md:col-4">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="name"
                    >
                        {t("version.name")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="name"
                            name="name"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        name: {
                                            ...lazyParams.filters.name,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            <div className="col-12 md:col-4 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                setCheckLoad(true);
                                setLazyParams({
                                    ...lazyParams,
                                    first: 0,
                                    page: 0,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("version.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("version.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={appVersions}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    selectionMode="single"
                                    selection={selectedAppVersion}
                                    onSelectionChange={(e) => {
                                        setSelectedAppVersion(e.value);
                                    }}
                                    // globalFilter={globalFilter}
                                    // header={headerTemplate}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    // rowsPerPageOptions={[10, 20, 50]}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="appType" header={t("version.appType")} body={typeTemplate} sortable style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="name" header={t("name")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="description" header={t("description")} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="modifiedDate" header={t("modifiedDate", { ns: "common" })} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="modifiedBy.username" header={t("modifiedBy", { ns: "common" })} />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">{actionTemplate()}</div>
                    </div>

                    <Confirmation
                        visible={deleteAppVersionDialog}
                        hideAction={hideDeleteAppVersionDialog}
                        processAction={deleteAppVersionAction}
                        confirmMessage={
                            selectedAppVersion && (
                                <span>
                                    {t("confirm.delete", { ns: "common", object: t("version") })} <b>{selectedAppVersion.name}</b>?
                                </span>
                            )
                        }
                    />
                    <AppVersionEditor />
                </div>
            </div>
        </div>
    );
};
